/**
 * THE-FIRST-BUILD-SCREEN-1 — what happens between a verified email and a build.
 *
 * NAMED FOR THE SCREEN, not the build. `firstBuild.ts` is PLANNER-2's, landed
 * on main while this wave was being written; this one is the state of the
 * SCREEN a verified person lands on, which is a different question.
 *
 * The owner's ruling: "after signup with email verified, never before, the
 * whole software is built in one shot", and "what they typed carries through
 * to the real build". So the screen a verified person lands on has one job:
 * attach the sentence they typed to the account, ask the planner with it, and
 * hand them to the plan they will approve. It builds nothing itself.
 *
 * THE THREE STATES MUST NOT LOOK THE SAME, which is why `asked` and `refused`
 * and `stuck` are three answers and not one: Ghost declining to plan is not
 * Ghost being unreachable, and neither is a person arriving with no sentence.
 */

import { COULD_NOT_FINISH } from './straightThrough'
import { isNoCredits, refusalWords } from './firstBuildTruth'

/** Is this person allowed past the door at all. */
export function isVerified(user: { email_confirmed_at?: unknown } | null | undefined): boolean {
  const at = user?.email_confirmed_at
  return typeof at === 'string' && at.trim() !== ''
}

/** What the first build screen is doing, and what a person is reading. */
export type FirstBuildStep =
  | { at: 'waiting' }
  | { at: 'nothing-typed' }
  | { at: 'planning'; sentence: string }
  | { at: 'ready'; sentence: string; planId: string }
  /** THE-FIRST-BUILD-RUNS-STRAIGHT-THROUGH-1 — approved and being watched. */
  | { at: 'building'; sentence: string; planId: string }
  /** The build ended without being built. */
  | { at: 'failed'; sentence: string }
  /**
   * THE-FIRST-BUILD-TELLS-THE-TRUTH-1 — `credits` marks the refusal a person
   * answers by adding credits, so the page can show where that is done.
   */
  | { at: 'refused'; sentence: string; why: string; credits?: boolean }
  | { at: 'stuck'; sentence: string; why: string }

/**
 * WHAT TO DO WITH THE ANSWER THE PLANNER GAVE. A plan id is the only thing
 * that moves a person on; anything else is said in Ghost's own words where it
 * gave them, and never paraphrased into a failure.
 */
export function stepFromAnswer(sentence: string, answer: unknown): FirstBuildStep {
  const res = answer as { ok?: unknown; plan_id?: unknown } | null
  if (res && res.ok === true && typeof res.plan_id === 'string' && res.plan_id !== '') {
    return { at: 'ready', sentence, planId: res.plan_id }
  }
  // THE-FIRST-BUILD-TELLS-THE-TRUTH-1 — the planner's own words, wherever it
  // put them. The stock line is only for an answer that carries no sentence.
  return {
    at: 'refused',
    sentence,
    why: refusalWords(answer) ?? 'Ghost had nothing to build from that.',
    credits: isNoCredits(answer),
  }
}

/** Could not ask at all, which is not the same as being told no. */
export function stuckFrom(sentence: string, why: unknown): FirstBuildStep {
  const said = typeof why === 'string' ? why.trim() : ''
  return {
    at: 'stuck',
    sentence,
    why: said === '' ? 'Ghost could not be reached just now.' : said,
  }
}

/** The one line the screen says while it is doing this. */
export function sayingFor(step: FirstBuildStep): string {
  switch (step.at) {
    case 'waiting':
      return 'One moment.'
    case 'nothing-typed':
      return 'Tell Ghost what your business does and it will build the software that runs it.'
    case 'planning':
      return 'Ghost is working out what to build you.'
    case 'ready':
      return 'Ghost has a plan.'
    case 'building':
      // The build speaks for itself, in the life line's own words.
      return ''
    case 'failed':
      return COULD_NOT_FINISH
    // A refusal and an outage each say their OWN reason, never a stock line.
    case 'refused':
    case 'stuck':
      return step.why
  }
}

/** A person can try again only when trying again could change the answer. */
export function canTryAgain(step: FirstBuildStep): boolean {
  return step.at === 'stuck' || step.at === 'failed'
}
